import { helpers } from 'veritone-redux-common';
import {
    UPDATE_SEARCH_QUERY,
    CLEAR_SEARCH_QUERY,
    FETCH_AUTOCOMPLETE_RESULTS,
    FETCH_AUTO_COMPLETE_RESULTS_SUCCESS,
    FETCH_AUTO_COMPLETE_RESULTS_FAILURE,
    FETCH_AUTO_COMPLETE_RESULTS_RESET,
    updateSearchQuery,
    clearSearchQuery,
    fetchAutoCompleteResults,
    fetchAutoCompleteResultsFailure,
    fetchAutoCompleteResultsSuccess,
    searchSelector,
    moduleId
} from './const';
import { takeLatest, put, select } from 'redux-saga/effects';
import { notificationsModule } from 'veritone-widgets';
import { get } from 'lodash';
import { Status as advertiserStatus } from '../../.././../state/modules/app/advertiser';
import {  Status as userStatus } from '../../.././../state/modules/app/userManagement/actions';

const { showNotification } = notificationsModule;
const { createReducer } = helpers;

/**
 * State Shape
 * {
 *   query: string,
 *   loading: bool,
 *   results: array
 * }
 */
export const searchReducer = createReducer(
    {
        query: '',
        loading: false,
        results: {}
    },
    {
        [UPDATE_SEARCH_QUERY]: (state, { payload }) => ({
            ...state,
            query: payload
        }),
        [CLEAR_SEARCH_QUERY]: (state, payload) => ({
            query: '',
            results: {
              campaigns: [],
              advertisers: []
            },
            loading: false
        }),

        // auto complete
        [FETCH_AUTOCOMPLETE_RESULTS]: state => ({
            ...state,
            results: {
              campaigns: [],
              advertisers: []
            },
            loading: true
        }),
        [FETCH_AUTO_COMPLETE_RESULTS_SUCCESS]: (state, { payload }) => ({
            ...state,
            loading: false,
            results: payload
        }),
        [FETCH_AUTO_COMPLETE_RESULTS_FAILURE]: state => ({
            ...state,
            loading: false
        })
    }
);
export const UNASSIGNED_FOLDER_NAME = 'Unassigned';

/**
 * Returns auto complete results
 *
 * If there are performance issues in the future due to size of the data we could
 * build search index to avoid heavy processing here. If there is just too much data,
 * we can index this data in elastic search, and have elastic search perform the search.
 * See Library-App for the implementation.
 *
 * @returns {Generator<*, void, ?>}
 */
function* fetchAutoCompleteResultsFunc() {
    let query,
        users,
        currentUserId,
        advertisersOriginal,
        selfAdvertisersOriginal,
        teamAdvertisersOriginal,
        unassignedAdvertisersOriginal,
        campaignsNormalized,
        advertisersNormalized,
        campaignsSelected = [],
        advertisersSelected = [];

    const userLoadStatus = yield select(state => state.userManagement.users.status);
    const advertiserLoadStatus = yield select(state => state.advertiser.status);

    if (
      advertiserLoadStatus !== advertiserStatus.STATUS_LOADED ||
      userLoadStatus !== userStatus.STATUS_LOADED
    ) {
      // display loading icon while waiting for fetching data
      yield put(fetchAutoCompleteResults());
      return;
    };

    // get data we need, query is the search query
    query = yield select(state => searchSelector(state).query);
    advertisersOriginal = yield select(state => state.advertiser.byId);
    selfAdvertisersOriginal = yield select(state => state.advertiser.self);
    teamAdvertisersOriginal = yield select(state => state.advertiser.team);
    unassignedAdvertisersOriginal = yield select(state => state.advertiser.unassignedAdvertisers || []);
    currentUserId = yield select(state => state.user.user.userId);
    users = yield select(state => state.userManagement.users.data);

    query = query.toLowerCase();

    // get the current users
    const userHashMap = users && users.reduce((acc, user) => {
      if (user.id) {
        acc[user.id] = user;
      }
      return acc;
    }, {});

    //process available advertisers
    const selfAdvertisersHashMap = {};

    // get the advertisers created or shared with the current user.
    const currentUser = userHashMap[currentUserId];
    const currentUserFolderName = get(currentUser, 'fullName', '');
    selfAdvertisersOriginal.forEach(id => {
      const advertiser = advertisersOriginal[id];
      if (advertiser) {
        const key = `${id}_${currentUserId}`;
        selfAdvertisersHashMap[key] = {
          ...advertiser,
          folder: {
            name: currentUserFolderName
          }
        }
      }
    });

    //get the advertisers of the available users via teams.
   const teamAdvertisersHashMap = teamAdvertisersOriginal && teamAdvertisersOriginal.reduce((accum, teamMember) => {
     const memberAdvertisers = get(teamMember, 'advertisers', []);
     const memberId = get(teamMember, 'userId');
     const member = userHashMap[memberId];

     // Suppress the advertiser and campaigns (for now) from the search bar if the user has been deactivated.
     if (member) {
       const memberFolderName = get(member, 'fullName', '');
       memberAdvertisers.forEach(id => {
         const memberAdvertiser = advertisersOriginal[id];
         if (memberAdvertiser) {
           const key = `${id}_${memberId}`;
           accum[key] = {
             ...memberAdvertiser,
             folder: {
               name: memberFolderName
             }
           };
         }
       });
     }

     return accum;
   }, {});

  //get the advertisers of the unassigned users.
  const unassignedAdvertisersHashMap = unassignedAdvertisersOriginal.reduce((accum, user) => {
    const advertisers = get(user, 'advertisers', []);

    advertisers.forEach(id => {
      const unassignedAdvertiser = advertisersOriginal[id];
      if (unassignedAdvertiser) {
        accum[id] = {
          ...unassignedAdvertiser,
          folder: {
            name: UNASSIGNED_FOLDER_NAME
          }
        };
      }
    });

    return accum;
  }, {});

  const selfAdvertisersList = Object.values(selfAdvertisersHashMap);
  const teamAdvertisersList = Object.values(teamAdvertisersHashMap);
  const unassignedAdvertisersList = Object.values(unassignedAdvertisersHashMap);
  const availableAdvertisers = selfAdvertisersList.concat(teamAdvertisersList).concat(unassignedAdvertisersList);

   // get the available campaigns
   const availableCampaignsHashMap = {};

   availableAdvertisers.forEach(advertiser => {
     const campaigns = get(advertiser, 'campaigns', []);
     campaigns.forEach(campaign => {
       if (!availableCampaignsHashMap[campaign.campaign_id]) {
         const folderName = get(advertiser, 'folder.name');
         availableCampaignsHashMap[campaign.campaign_id] = {
           ...campaign,
           advertiser_id: advertiser.advertiser_id,
           advertiser_name: folderName === UNASSIGNED_FOLDER_NAME ? folderName : advertiser.advertiser_name
         }
       }
     })
   });

   const availableCampaigns = Object.values(availableCampaignsHashMap);

    // find items
    campaignsSelected = query ? availableCampaigns.filter(
                            item => (
                                item.campaign_name &&
                                item.campaign_name.toLowerCase().includes(query)
                                )
                            ) : availableCampaigns;
    advertisersSelected = query ? availableAdvertisers.filter(
                            item => (
                                item.advertiser_name &&
                                item.advertiser_name.toLowerCase().includes(query)
                            )
                          ) : availableAdvertisers;
    // normalize data
    campaignsNormalized = campaignsSelected.map(
        ({ campaign_id, campaign_name, advertiser_id, advertiser_name }) => ({
            id: campaign_id,
            name: campaign_name,
            advertiser: {
                id: advertiser_id,
                name: advertiser_name
            }
        })
    );

    advertisersNormalized = advertisersSelected.map(
        ({ advertiser_id, advertiser_name, folder }) => ({
            id: advertiser_id,
            name: advertiser_name,
            folder
        })
    );

    yield put(
        fetchAutoCompleteResultsSuccess({
            campaigns: campaignsNormalized,
            advertisers: advertisersNormalized,
        })
    );
}

export function* searchSagas() {
    yield takeLatest(UPDATE_SEARCH_QUERY, fetchAutoCompleteResultsFunc);
}

export function getSearchModule() {
    return {
        id: moduleId,
        reducerMap: {
            [moduleId]: searchReducer
        },
        sagas: [searchSagas]
    };
}

export default searchReducer;
export const namespace = moduleId;
